/* import __COLOCATED_TEMPLATE__ from './posting-create-overview.hbs'; */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { readOnly, equal, alias, sort, filter } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { POSTING_CREATE_DATE_RANGE_TYPE } from 'sh-www/constants/routes/posting-create';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { LOCATION } from 'sh-www/models/location';
import { CLASSIFICATION_CATEGORIES } from 'sh-www/models/classification';
import { escapeRegExp } from 'sh-www/utils/misc';
import { v4 } from 'ember-uuid';

const EXPERIENCE_TAG_LIMIT = 3;

const CLASS_NAME = 'SH-c-posting-create-overview';

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: '',
  CLASS_NAME,

  /**
   * Register services
   */
  moment: service(),
  state: service('states/routes/posting-create'),
  stateManager: service('state-managers/routes/posting-create'),
  errorTracker: service('error-tracker'),
  locationCommandService: service('commands/location-commands'),
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  experienceTagLimit: EXPERIENCE_TAG_LIMIT,

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  _assignmentTypePitch: ASSIGNMENT.TYPES.PITCH,
  _assignmentTypeStandard: ASSIGNMENT.TYPES.STANDARD,

  _assignmentTypePitchChecked: computed('state.updatedPostingType', function() {
    return this.state.updatedPostingType == ASSIGNMENT.TYPES.PITCH;
  }),

  _assignmentTypeStandardChecked: computed('state.updatedPostingType', function() {
    return this.state.updatedPostingType == ASSIGNMENT.TYPES.STANDARD;
  }),

  _selectedSubjectClassificationIdToKeyMap: computed('state.updatedClassificationSubjects.@each.{model,key}', function() {
    return this.state.updatedClassificationSubjects.reduce((accum, classification) => {
      accum[classification.model] = classification.key;
      return accum;
    }, {});
  }),

  _selectedSubjectClassificationLimitReached: computed('state.updatedClassificationSubjects.[]', function() {
    return this.state.updatedClassificationSubjects.length >= EXPERIENCE_TAG_LIMIT;
  }),

  _subjectClassifications: filter('state.availableClassifications.@each.category', function(classification) {
    return classification.category === CLASSIFICATION_CATEGORIES.CATEGORY_SUBJECT;
  }),

  _subjectClassificationOptions: computed(
    '_subjectClassifications.@each.{id,label}',
    '_selectedSubjectClassificationIdToKeyMap.@each.id',
  function() {
    const { _selectedSubjectClassificationIdToKeyMap } = this;

    return this._subjectClassifications
      .filter((classification) => !_selectedSubjectClassificationIdToKeyMap[classification.id])
      .map((classification) => ({
        key: classification.label,
        model: classification.id,
      }));
  }),

  _subjectClassificationOptionsFiltered: computed(
    '_subjectClassificationOptions.@each.key',
    'state.updatedClassificationSubjectValue',
  function() {
    const classificationOptions = this._subjectClassificationOptions;
    const classificationsValue = this.state.updatedClassificationSubjectValue;

    if (!classificationsValue) {
      return classificationOptions;
    }

    const escapedInternalValue = escapeRegExp(classificationsValue);
    const pattern = new RegExp(`(\\b${escapedInternalValue})`, 'i');

    return classificationOptions.filter(({ key }) => {
      return pattern.test(key);
    });
  }),

  _selectedIndustryClassificationIdToKeyMap: computed('state.updatedClassificationIndustries.@each.{model,key}', function() {
    return this.state.updatedClassificationIndustries.reduce((accum, classification) => {
      accum[classification.model] = classification.key;
      return accum;
    }, {});
  }),

  _selectedIndustryClassificationLimitReached: computed('state.updatedClassificationIndustries.[]', function() {
    return this.state.updatedClassificationIndustries.length >= EXPERIENCE_TAG_LIMIT;
  }),

  _industryClassifications: filter('state.availableClassifications.@each.category', function(classification) {
    return classification.category === CLASSIFICATION_CATEGORIES.CATEGORY_INDUSTRY;
  }),

  _industryClassificationOptions: computed(
    '_industryClassifications.@each.{id,label}',
    '_selectedIndustryClassificationIdToKeyMap.@each.id',
  function() {
    const { _selectedIndustryClassificationIdToKeyMap } = this;

    return this._industryClassifications
      .filter((classification) => !_selectedIndustryClassificationIdToKeyMap[classification.id])
      .map((classification) => ({
        key: classification.label,
        model: classification.id,
      }));
  }),

  _industryClassificationOptionsFiltered: computed(
    '_industryClassificationOptions.@each.key',
    'state.updatedClassificationIndustryValue',
  function() {
    const classificationOptions = this._industryClassificationOptions;
    const classificationsValue = this.state.updatedClassificationIndustryValue;

    if (!classificationsValue) {
      return classificationOptions;
    }

    const escapedInternalValue = escapeRegExp(classificationsValue);
    const pattern = new RegExp(`(\\b${escapedInternalValue})`, 'i');

    return classificationOptions.filter(({ key }) => {
      return pattern.test(key);
    });
  }),

    /**
   * @type {String}    Track candidate location input.
   */
  _candidateLocationInputValue: '',

  /**
   * @type {String}     Constants for the date range enums in the template file.
   */
  _constantDateRangeSelectADate: POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE,

  /**
   * @type {Date|null}    The current selected for the calendar input.
   */
  _dateStartingValue: computed('state.updatedDateSelected', function() {
    const currentSelectedDate = this.get('state.updatedDateSelected');

    if (!currentSelectedDate) {
      return null;
    }

    return currentSelectedDate.toDate();
  }),

  /**
   * @type {Boolean}    True when we have a deadline date error.
   */
  _isHasDeadlineDateError: readOnly('state.isDeadlineDateExpired'),

  /**
   * @type {Boolean}    Boolean for whether candidate location is global
   */
  _isCandidateGlobal: readOnly('state.isCandidateGlobal'),

  /**
   * @type {Boolean}    Boolean for whether Work Remotely is checked
   */
  _isGlobal: readOnly('state.isGlobal'),

  /**
   * @type {Boolean}    Whether to show second checkbox for entering a candidate location
   */
  _isCandidateLocationEnabled: readOnly('state.usingCandidateLocation'),

  /**
   * @type {String}    Track location input. Default to address.
   */
  _locationInputValue: '',

  /**
   * @type {Boolean}    Booleans for the block buttons for what type of date range is selected.
   */
  _isSelectADateSelected: equal('_updatedDateRangeType', POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE),

  /**
   * This is for the 'select' mode
   *
   * @type {Date}       The minimum date selectable for the select a date, date picker
   */
  _minDateAllowed: computed(function() {
    return this.get('moment').moment().add(1, 'day').toDate();
  }),

  /** @type {Array<String>} */
  serviceSortKeys: ['category', 'name'],

  /** @type {Ember.A<Service>} */
  sortedServices: sort('state.availableServices', 'serviceSortKeys'),

  /**
   * @type {String}   This is the title of the date selection block button.  Will change based on if date is selected.
   *
   */
  _selectADateTitle: computed(
  'state.updatedDateSelected',
  'state.updatedDateRangeType',
  function() {
    if (this.get('state.updatedDateSelected') && this.get('state.updatedDateRangeType') === POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE) {
      return this.get('state.updatedDateSelected').format('MM/DD/YYYY');
    }

    return 'Select a date';
  }),

  _userIdToCompanyMembershipMap: computed('state.companyMemberships.[]', function() {
    return this.state.companyMemberships.reduce((accum, companyMembership) => {
      accum[companyMembership.user.id] = companyMembership;
      return accum;
    }, {});
  }),

  /**
   * @type {String}     of type POSTING_CREATE_DATE_RANGE_TYPE.*
   */
  _updatedDateRangeType: readOnly('state.updatedDateRangeType'),

  /**
   * @type {Boolean|null}   True when the user is ready to make offer - null if never selected
   */
  _updatedDirectIsReadyToMakeOffer: readOnly('state.updatedDirectIsReadyToMakeOffer'),

  /**
   * @type {Boolean}    True if this is an urgent assignment.
   */
  _updatedIsUrgent: readOnly('state.updatedIsUrgent'),

  /**
   * @type {Boolean}    True if this is direct hire mode.
   */
  _isDirectHireMode: readOnly('state.isDirectHireMode'),

  /**
   * @type {Boolean}    When this is true we will trigger the date selector in our form.
   */
  _isShowDateSelector: false,

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {Object}   The object map for errors being returned from the server.
   */
  _errors: alias('state.errors'),

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {Location|null}   The location model for candidate selected by the user or null
   */
  _updatedCandidateLocation: alias('state.updatedCandidateLocation'),

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {Location|null}   The location model selected by the user or null
   */
  _updatedLocation: alias('state.updatedLocation'),

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {Ember.MutableArray<Object>}   Array of the option items that have the country names and ids.
   */
  _updatedCountryOptions: alias('state.updatedCountryOptions'),

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {Ember.MutableArray<Country>} The available countries, sorted.
   */
  _availableCountriesSorted: alias('state.availableCountriesSorted'),

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {Ember.MutableArray<Region>}    All available regions loaded from the server.
   */
  _availableRegions: alias('state.availableRegions'),

  /**
   * NOTE - THIS WILL MODIFY THE MAIN STATE.  This array's content will change as it is passed into selector by reference.
   *
   * @type {Ember.A<Service>}
   */
  _updatedSelectedServices: readOnly('state.updatedSelectedServices'),

  /**
   * NOTE - THIS IS ALIASED WITH THE ROUTE LEVEL STATE
   *
   * @type {String}   The title that has been updated
   */
  _updatedTitle: alias('state.updatedTitle'),

  /**
   * @param {string} value
   */
  @action
  handleCandidateLocationInput(value) {
    this.setProperties({
      'state.errors': {
        ...this.state.errors,
        candidate_place_id: undefined,
      },
      _candidateLocationInputValue: value,
    });

    this.set('_candidateLocationInputValue', value);

    // clear selection if they typed
    this.stateManager.updateCandidateLocation(null);
  },

  @action
  handleIndustriesClassificationBlur() {
    this.stateManager.updateClassificationIndustryValue('');
  },

  /**
   * @param {string} value
   */
  @action
  handleIndustriesClassificationInput(value) {
    this.stateManager.updateClassificationIndustryValue(value);
  },

  @action
  handleSubjectClassificationBlur() {
    this.stateManager.updateClassificationSubjectValue('');
  },

  /**
   * @param {string} value
   */
  @action
  handleSubjectClassificationInput(value) {
    this.stateManager.updateClassificationSubjectValue(value);
  },

  /**
   * @param {Classification} classification
   */
  @action
  handleIndustriesClassificationAdded(classification) {
    this.stateManager.addClassificationIndustry(classification);
  },

  /**
   * @param {Classification} classification
   */
  @action
  handleCustomIndustriesClassificationAdded(classification) {
    this.stateManager.addClassificationIndustry({
      ...classification,
      category: CLASSIFICATION_CATEGORIES.CATEGORY_INDUSTRY,
      isCustom: true,
      model: v4(), // do not filter out multiple custom chips
    });
  },

  /**
   * @param {Classification} classification
   */
  @action
  handleSubjectsClassificationAdded(classification) {
    this.stateManager.addClassificationSubject(classification);
  },

  /**
   * @param {Classification} classification
   */
  @action
  handleCustomSubjectsClassificationAdded(classification) {
    this.stateManager.addClassificationSubject({
      ...classification,
      category: CLASSIFICATION_CATEGORIES.CATEGORY_SUBJECT,
      isCustom: true,
      model: v4(), // do not filter out multiple custom chips
    });
  },

  /**
   * @param {Classification} classification
   */
  @action
  handleIndustriesClassificationRemoved(classification) {
    this.stateManager.removedClassificationIndustry(classification);
  },

  /**
   * @param {Classification} classification
   */
  @action
  handleSubjectsClassificationRemoved(classification) {
    this.stateManager.removeClassificationSubject(classification);
  },

  /**
   * @param {string} value
   */
  @action
  handleTitleInput(value) {
    this.set('state.errors', {
      ...this.state.errors,
      title: undefined,
    });

    this.stateManager.updateTitleValue(value);
  },

  /**
   * @param {string} value
   */
  @action
  handleLocationInput(value) {
    this.setProperties({
      'state.errors': {
        ...this.state.errors,
        place_id: undefined,
      },
      _locationInputValue: value,
    });

    // clear selection if they typed
    this.stateManager.updateLocation(null);
  },

  /**
   * @param {DS.Model} service
   */
  @action
  handleServiceAdded(service) {
    this.set('state.errors', {
      ...this.state.errors,
      services: undefined,
    });

    this.state.updatedSelectedServices.pushObject(service);
  },

  /**
   * @param {DS.Model} service
   */
  @action
  handleServiceRemoved(service) {
    this.set('state.errors', {
      ...this.state.errors,
      services: undefined,
    });

    this.state.updatedSelectedServices.removeObject(service);
  },

  actions: {

    /**
     * This is called when the user selects a date from our date selector
     *
     * @param {Date} date
     */
    onDateSelected: function(date) {
      this.stateManager.updateDateRangeType(POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE);

      this.get('stateManager').updateDeadlineDate(this.get('moment').moment(date));
      this.set('_isShowDateSelector', false);
    },

    /**
     * This is closed when the date selector input has closed.
     */
    onDateSelectorClosed: function() {
      this.set('_isShowDateSelector', false);
    },

    /**
     * Called when the project is able to be worked remotely
     */
    toggleLocationGlobal() {
      const { _updatedLocation } = this;

      // always clear input and related errors
      this.setProperties({
        'state.errors': {
          ...this.state.errors,
          place_id: undefined,
        },
        _locationInputValue: '',
      });

      // toggle off
      if (_updatedLocation?.locationType === LOCATION.TYPES.GLOBAL) {
        this.stateManager.updateLocation(null);
        return;
      }

      // toggle on
      const globalLocation = {
        locationType: LOCATION.TYPES.GLOBAL,
      };

      this.stateManager.updateLocation(globalLocation);
    },

    /**
     * Called when the candidate location ability is enabled
     */
    onUseCandidateLocation() {
      this.set('state.errors', {
        ...this.state.errors,
        candidate_place_id: undefined,
      });

      const { _isCandidateLocationEnabled } = this;
      this.stateManager.toggleUseCandidateLocation(!_isCandidateLocationEnabled);
    },

    /**
     * When mouse leaves, close the date selector if it's open.
     */
    onMouseLeaveCalendar: function() {
      this.set('_isShowDateSelector', false);
    },

    /**
     * Called when the country location changes within the location selector.
     *
     * @param {Ember.MutableArray<Object>} selectedOptions   The  options for the multi-select that the user has selcted.
     * @param {Boolean=} opt_isReplace    True if you want to replace all selected.
     */
    onCountryLocationChange(selectedOptions, opt_isReplace) {
      this.get('stateManager').updateSelectedCountries(selectedOptions, opt_isReplace);
    },

    onCountryLocationRemoved(selectedOption) {
      this.stateManager.removeSelectedCountries(selectedOption);
    },

    /**
     * Called when the city location changes within the location selector.
     *
     * @param {Location|null} location
     */
    onCityLocationChange(location) {
      this.get('stateManager').updateCityLocation(location);
    },

    /**
     * Called when the user selects a date range enum.
     *
     * @param {String} dateRangeTypeSelected    of type POSTING_CREATE_DATE_RANGE_TYPE.*
     */
    onSelectDateTypeButtonClicked: function(dateRangeTypeSelected) {
      this.setProperties({
        'state.errors': {
          ...this.state.errors,
          deadline: undefined,
        },
        _isShowDateSelector: dateRangeTypeSelected === POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE,
      });

      this.get('stateManager').updateDateRangeType(dateRangeTypeSelected);
    },

    /**
     * Called when the user selects a date range enum.
     *
     * @param {String} postingType    of type ASSIGNMENT.TYPES.
     */
    onPostingTypeChange: function(postingType) {
      this.set('state.errors', {
        ...this.state.errors,
        type: undefined,
      });

      this.stateManager.setPostingType(postingType);

      if (postingType !== ASSIGNMENT.TYPES.PITCH) {
        return;
      }

      // if date range was set before the post type was set to pitch
      const selectedDate = this.state.updatedDateSelected;
      if (selectedDate) {
        const todaysDate = this.moment.moment();
        const daysAway = selectedDate.diff(todaysDate, 'days');

        // cannot fill value using this type
        if (daysAway < 7) {
          this.state.setProperties({
            updatedDateSelected: null,
            updatedDateRangeType: ''
          });
        }
      }
    },
  },

  /**
   * Update candidate location
   *
   * @param {Location} locationRecord Location record that has not been saved.
   */
  updateCandidateLocation: task(function *(locationRecord) {
    yield this.stateManager.updateCandidateLocation(locationRecord);
  }),

  /**
   * Update project location
   *
   * @param {Location} locationRecord Location record that has not been saved.
   */
  updateProjectLocation: task(function *(locationRecord) {
    yield this.stateManager.updateLocation(locationRecord);
  }),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);

    this.set('_locationInputValue', this.state.updatedLocation?.formattedAddress ?? '');

    const { updatedCandidateLocation } = this.state;

    if (updatedCandidateLocation?.locationType !== LOCATION.TYPES.GLOBAL) {
      this.set('_candidateLocationInputValue', updatedCandidateLocation?.formattedAddress ?? '');
    }
  }
});
